// components/ui/DropdownMenu.tsx — Context menu for tree nodes and actions

import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import styles from './DropdownMenu.module.css';

export interface DropdownMenuItem {
  id: string;
  label: string;
  icon?: string;
  onSelect: () => void;
  isDestructive?: boolean;
  isDisabled?: boolean;
}

export interface DropdownMenuGroup {
  label?: string;
  items: DropdownMenuItem[];
}

interface DropdownMenuProps {
  trigger: ReactNode;
  groups: DropdownMenuGroup[];
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

/**
 * DropdownMenu — glass-style context menu used for tree node long-press actions.
 * Wraps Radix DropdownMenu with Folk Of Interest design system styles.
 */
export function DropdownMenu({
  trigger,
  groups,
  side = 'bottom',
  align = 'start',
}: DropdownMenuProps) {
  return (
    <RadixDropdown.Root>
      <RadixDropdown.Trigger asChild>
        {trigger}
      </RadixDropdown.Trigger>

      <RadixDropdown.Portal>
        <RadixDropdown.Content
          className={styles.content}
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={16}
        >
          {groups.map((group, groupIdx) => (
            <RadixDropdown.Group key={groupIdx}>
              {group.label && (
                <RadixDropdown.Label className={styles.label}>
                  {group.label}
                </RadixDropdown.Label>
              )}

              {group.items.map((item, itemIdx) => {
                const isFirst = groupIdx === 0 && itemIdx === 0;
                const showSeparator = !isFirst && itemIdx === 0 && groupIdx > 0;

                return (
                  <span key={item.id}>
                    {showSeparator && <RadixDropdown.Separator className={styles.separator} />}
                    <RadixDropdown.Item
                      className={item.isDestructive ? styles.itemDestructive : styles.item}
                      {...(item.isDisabled !== undefined ? { disabled: item.isDisabled } : {})}
                      onSelect={item.onSelect}
                    >
                      {item.icon && (
                        <span className={styles.itemIcon} aria-hidden="true">
                          {item.icon}
                        </span>
                      )}
                      {item.label}
                    </RadixDropdown.Item>
                  </span>
                );
              })}
            </RadixDropdown.Group>
          ))}
        </RadixDropdown.Content>
      </RadixDropdown.Portal>
    </RadixDropdown.Root>
  );
}
